import IconInfo from '@apollo/icons/default/IconInfo.svg';
import { ListItem, Text, Tooltip } from '@apollo/orbit';
import classnames from 'classnames';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';

import { insightsRouteConfig } from 'src/app/graph/routes';
import { MutationBadge } from 'src/components/common/operationTypeBadge/MutationBadge';
import { SubscriptionBadge } from 'src/components/common/operationTypeBadge/SubscriptionBadge';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { format } from 'src/lib/format';
import { OperationType } from 'src/lib/graphqlTypes/types';
import {
  NormalizedMetricsField,
  NormalizedOperationsQueryStats,
  formatMetrics,
} from 'src/lib/metrics';
import { RouteConfig } from 'src/lib/routeConfig/RouteConfig';
import {
  isOperationUnexecutable,
  unexecutableOperationReason,
} from 'src/lib/unexecutableOperationReason';

import { OperationIdBadge } from '../../../OperationIdBadge';

import { HighlightedListItemName } from './HighlightedListItemName';

interface OperationsProps extends NormalizedOperationsQueryStats {
  id: string;
  name?: string;
  type?: OperationType | null;
  statsSortKey?: NormalizedMetricsField;
  isSelected: boolean;
  hideStatsValue?: boolean;
  className?: string;
  children?: React.ReactNode;
  loading: boolean;
}

export const OperationsListItem = ({
  id,
  name,
  type,
  statsSortKey,
  isSelected,
  hideStatsValue,
  children,
  className,
  loading,
  ...otherQueryStatsProps
}: OperationsProps) => {
  const location = useLocation();
  const { operationSearch } = useRouteParams(insightsRouteConfig);
  const displayName = name || '[query signature unavailable]';

  let statsDisplayValue: string | number | null | undefined =
    statsSortKey === 'alphabetically'
      ? otherQueryStatsProps.requestsCount
      : statsSortKey && otherQueryStatsProps[statsSortKey];
  if (statsSortKey && statsDisplayValue) {
    const splitMetrics = format.splitUnits(
      formatMetrics(statsSortKey, statsDisplayValue),
    );
    statsDisplayValue = `${splitMetrics.value}${splitMetrics.unit}`;
  }

  return (
    <ListItem isSelected={isSelected}>
      <Link
        to={insightsRouteConfig.locationFrom({
          location,
          patch: {
            query: id ?? RouteConfig.DELETE,
            queryName: name ?? RouteConfig.DELETE,
            trace: RouteConfig.DELETE,
            traceBucket: RouteConfig.DELETE,
            errorsDrilldown: RouteConfig.DELETE,
          },
        })}
        className={classnames(className, 'flex w-full items-center truncate')}
      >
        <>
          {isOperationUnexecutable(displayName) ? (
            <>
              <span title={displayName} className="truncate italic">
                {displayName}
              </span>
              <Tooltip label={unexecutableOperationReason(displayName)}>
                <IconInfo className="ml-2" />
              </Tooltip>
            </>
          ) : (
            <span title={displayName} className="truncate">
              <HighlightedListItemName
                itemName={displayName}
                searchTerm={operationSearch}
                loading={loading}
              />
            </span>
          )}
          <div className="mr-4 flex">
            {id && <OperationIdBadge className="ml-1.5 mr-1" id={id} />}

            {type === OperationType.MUTATION ? (
              <MutationBadge disableTooltip={false} />
            ) : type === OperationType.SUBSCRIPTION ? (
              <SubscriptionBadge disableTooltip={false} />
            ) : null}
          </div>
        </>
        <Text className="ml-auto text-secondary" variant="code" size="sm">
          {hideStatsValue
            ? null
            : statsDisplayValue === null || statsDisplayValue === undefined
              ? 'n/a'
              : statsDisplayValue}
        </Text>
      </Link>
    </ListItem>
  );
};
